import { bootstrapSession, type IdentitySession } from "@mountayaapp/auth";
import type { IdentityStoreApi } from "@mountayaapp/identity";

import { applyAnalyticsPolicy, identifySession } from "../../analytics/rules/identify";
import { ory } from "./ory";

/**
 * pending caches the in-flight (or settled) session bootstrap so the root
 * beforeLoad — which re-runs on every client navigation — fires the Ory whoami
 * exactly once per app load. This module-scoped cache is safe because the auth
 * flows redirect via full-page navigation (useAuthFlow's navigate ->
 * window.location.assign), which tears down and reboots the module on each login
 * / logout, resetting the cache. So there is no stale-session loop where a
 * logged-out user keeps reading a cached authenticated session.
 */
let pending: Promise<IdentitySession | null> | null = null;

/**
 * ensureSession runs the Ory session bootstrap once and hydrates the injected
 * identity store from the parsed value object (or resets it to anonymous). It
 * resolves with the session so the root beforeLoad can place it on the router
 * context for child guards to read. The guard runs outside React (beforeLoad),
 * so it writes the store directly through the injected store handle rather than
 * the theme's useIdentityStore selector seam. A malformed identity
 * (bootstrapSession throws a ZodError) or a non-401 whoami failure degrades to
 * anonymous rather than leaving a dangling rejection.
 *
 * This is also where the analytics opt-out is applied, because it is the first
 * moment the signed-in email exists: PostHog started at bootstrap, one round-trip
 * earlier, so an excluded person is classified here rather than before init.
 */
export const ensureSession = (store: IdentityStoreApi): Promise<IdentitySession | null> => {
  if (!pending) {
    pending = bootstrapSession(ory)
      .then((session) => {
        if (session) {
          store.getState().setSession(session);
          applyAnalyticsPolicy(session);

          return session;
        }

        store.getState().reset();

        return null;
      })
      .catch(() => {
        store.getState().reset();

        return null;
      });
  }

  return pending;
};

/**
 * refreshSession re-runs the Ory whoami and re-hydrates the identity store from
 * the fresh value object, returning the new session. It is called after an Ory
 * self-service settings submit succeeds (a trait/password change), so the store
 * reflects the updated identity without a full-page reload. The new bootstrap
 * promise is assigned to the module `pending` cache SYNCHRONOUSLY before the
 * await, so a concurrent ensureSession (the root beforeLoad re-running on a
 * navigation that overlaps the refresh) joins this refresh rather than reading
 * the stale cached session.
 *
 * A fresh session is written with setSession, which touches only what the whoami
 * carries: the membership the organization lookup resolved lands on a different
 * axis and is left alone. A genuine 401 (null session) resets the store to
 * anonymous. A transient whoami failure (the bootstrap throws) leaves the store
 * untouched and restores the previous cached value: a settings save that just
 * succeeded must not visually log the user out because the follow-up whoami
 * hiccuped.
 */
export const refreshSession = (store: IdentityStoreApi): Promise<IdentitySession | null> => {
  const previous = pending;

  pending = bootstrapSession(ory)
    .then((session) => {
      if (session) {
        store.getState().setSession(session);

        // The email is the one trait that can move a person across the analytics
        // policy, so re-apply it before identifying. Order matters in both
        // directions: a newly excluded person must already be opted out when the
        // identify below runs, and a person who is no longer excluded must be
        // readmitted first or that identify is dropped.
        applyAnalyticsPolicy(session);

        // A settings save is the one moment a trait actually changes, so push the
        // refreshed profile rather than waiting for the next page load. This is the
        // unconditional identify, not the deduplicated one: the account id has not
        // changed, only what is attached to it.
        identifySession(store);

        return session;
      }

      store.getState().reset();

      return null;
    })
    .catch(() => {
      // A transient whoami failure after a successful save: keep the store as it
      // is and fall back to whatever the cache held before this refresh, so the
      // user is not bounced to anonymous on a network hiccup.
      pending = previous;

      return previous ?? Promise.resolve(null);
    });

  return pending;
};
